import { useStableCallback } from "@prismicio/editor-support/React"
import { filter, slice } from "@prismicio/editor-support/Set"
import {
  type KeyboardEvent,
  type RefObject,
  useLayoutEffect,
  useState,
} from "react"

const gapBetweenTags = 8
const maxInputContainerSize = 225

interface UseTagFieldHeaderProps {
  selectedValues: Set<string>
  tagsContainerRef: RefObject<HTMLDivElement>
  open: boolean
  onSelectedValuesChange: (selectedValues: Set<string>) => void
}

export function useTagFieldHeader(props: UseTagFieldHeaderProps) {
  const { selectedValues, tagsContainerRef, open, onSelectedValuesChange } =
    props

  const [closeDisplayedTags, setClosedDisplayedTags] = useState(selectedValues)

  const updateDisplayedTagsOnClose = useStableCallback(
    (container: HTMLDivElement) => {
      const containerWidth = container.clientWidth
      const tagList = Array.from(container.children || [])

      const maxTagsContainerWidth = containerWidth - maxInputContainerSize

      const { displayedTags } = calculateDisplayedTags({
        tagList,
        maxTagsContainerWidth,
      })

      setClosedDisplayedTags(new Set(displayedTags))
    },
  )

  useLayoutEffect(() => {
    if (tagsContainerRef.current) {
      updateDisplayedTagsOnClose(tagsContainerRef.current)
    }
  }, [selectedValues, updateDisplayedTagsOnClose, tagsContainerRef])

  const extraTags = selectedValues.size - closeDisplayedTags.size

  const displayedSelectedValues = open ? selectedValues : closeDisplayedTags

  function removeSelectedValue(valueToDelete: string | LastItemLocation) {
    if (valueToDelete === -1) {
      const updatedValues = slice(selectedValues, 0, selectedValues.size - 1)
      return onSelectedValuesChange(updatedValues)
    }

    const filteredValues = filter(
      selectedValues,
      (value) => value !== valueToDelete,
    )
    return onSelectedValuesChange(filteredValues)
  }

  return {
    removeSelectedValue,
    displayedSelectedValues,
    extraTags,
  }
}

export type LastItemLocation = -1

interface calculateDisplayedTags {
  tagList: Element[]
  maxTagsContainerWidth: number
}
function calculateDisplayedTags(props: calculateDisplayedTags) {
  const { tagList, maxTagsContainerWidth } = props

  return tagList.reduce<{
    displayedTags: string[]
    tagsContainerWidth: number
  }>(
    ({ displayedTags, tagsContainerWidth }, tag) => {
      if (!(tag instanceof HTMLDivElement) || !tag.textContent)
        return { displayedTags, tagsContainerWidth }

      const currentContainerWidth =
        tagsContainerWidth + tag.offsetWidth + gapBetweenTags

      if (
        tag.dataset["marker"] === "badge" &&
        currentContainerWidth <= maxTagsContainerWidth
      )
        return {
          displayedTags: [...displayedTags, tag.textContent?.trim()],
          tagsContainerWidth: currentContainerWidth,
        }

      return { displayedTags, tagsContainerWidth }
    },
    { displayedTags: [], tagsContainerWidth: 0 },
  )
}

interface UseHandleKeyboardEntriesProps {
  inputRef: RefObject<HTMLDivElement>
  searchText: string
  setOpen: (open: boolean) => void
  onCreateTag: () => void
  removeSelectedValue: (valueToDelete: string | -1) => void
  setSearchText: (searchText: string) => void
}

export function useHandleKeyboardEntries(props: UseHandleKeyboardEntriesProps) {
  const {
    inputRef,
    searchText,
    onCreateTag,
    setOpen,
    removeSelectedValue,
    setSearchText,
  } = props

  function onBackspace() {
    if (searchText === "") removeSelectedValue(-1)
  }

  function onKeyDown(evt: KeyboardEvent) {
    if (evt.code === "Escape") {
      inputRef.current?.blur()
      setOpen(false)
      setSearchText("")
    }
    if (evt.key === "Backspace") onBackspace()
    if (evt.key === "Enter" && searchText !== "") onCreateTag()
  }

  return { onKeyDown }
}
